import React, { useState } from 'react';
import { X, Bell, Users, CreditCard, Settings, CheckCircle, ArrowRight, Clipboard } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const OnboardingModal = ({ isOpen, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const navigate = useNavigate();

  const steps = [
    {
      id: 'welcome',
      title: 'Welcome to Twachanga!',
      description: 'Your complete group management solution for contributions, loans, and member communication',
      icon: Users,
      color: 'bg-blue-500',
      items: [
        'Manage group members and their information',
        'Track contributions and payments',
        'Handle loans and repayments',
        'Send bulk SMS notifications',
        'Monitor group activities and reports'
      ]
    },
    {
      id: 'members',
      title: 'Manage Members',
      description: 'Add and manage your group members efficiently',
      icon: Users,
      color: 'bg-green-500',
      items: [
        'Add new members with their details',
        'View member profiles and contact info',
        'Track member contributions history',
        'Export member data and reports'
      ]
    },
    {
      id: 'contributions',
      title: 'Manage Contributions',
      description: 'Track and manage all group contributions and payments',
      icon: Clipboard,
      color: 'bg-purple-500',
      items: [
        'Record member contributions',
        'Track payment history and balances',
        'Generate contribution reports',
        'Set up M-Pesa payment integration',
        'Send payment reminders to members'
      ]
    },
    {
      id: 'loans',
      title: 'Manage Loans',
      description: 'Handle loan applications, approvals, and repayments',
      icon: CreditCard,
      color: 'bg-orange-500',
      items: [
        'Create loan categories and types',
        'Process loan applications',
        'Manage fines and penalties'
      ]
    },
    {
      id: 'communication',
      title: 'Communication & Notifications',
      description: 'Keep your group informed with bulk SMS and notifications',
      icon: Bell,
      color: 'bg-red-500',
      items: [
        'Send bulk SMS to all members',
        'Configure notification preferences',
        'Welcome new members automatically',
        'Send payment and loan reminders',
        'Manage SMS quota and billing'
      ]
    }
  ];

  const handleStepComplete = (stepId) => {
    if (!completedSteps.includes(stepId)) {
      setCompletedSteps([...completedSteps, stepId]);
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // All steps completed
      localStorage.setItem('onboardingCompleted', 'true');
      onComplete();
      // Redirect to settings page
      navigate('/settings');
    }
  };

  const handleSkip = () => {
    localStorage.setItem('onboardingCompleted', 'true');
    onComplete();
  };

  const currentStepData = steps[currentStep];
  const IconComponent = currentStepData.icon;

  if (!isOpen) return null;

  return (
    <div className="flex fixed inset-0 z-50 justify-center items-center p-4 bg-black bg-opacity-50">
      <div className="group bg-white rounded-2xl shadow-2xl w-full max-w-[95vw] sm:max-w-xl md:max-w-2xl lg:max-w-3xl max-h-[92vh] overflow-hidden">
        {/* Header */}
        <div className="relative p-6 border-b border-gray-100">
          <button
            onClick={handleSkip}
            className="absolute top-4 right-4 text-gray-400 transition-colors hover:text-gray-600"
          >
            <X size={24} />
          </button>
          
          <div className="flex items-center space-x-4">
            <div className={`p-3 rounded-full ${currentStepData.color} text-white`}>
              <IconComponent size={24} />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                Welcome to Twachanga! 🎉
              </h2>
              <p className="mt-1 text-gray-600">
                Let's set up your group for success
              </p>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-4 bg-gray-50">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">
              Step {currentStep + 1} of {steps.length}
            </span>
            <span className="text-sm text-gray-500">
              {Math.round(((currentStep + 1) / steps.length) * 100)}% Complete
            </span>
          </div>
          <div className="w-full h-2 bg-gray-200 rounded-full">
            <div
              className="h-2 bg-gradient-to-r from-blue-500 to-green-500 rounded-full transition-all duration-300"
              style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 overflow-y-auto max-h-[48vh] sm:max-h-[56vh] md:max-h-[60vh]">
          <div className="mb-8 text-center">
            <h3 className="mb-2 text-xl font-semibold text-gray-800">
              {currentStepData.title}
            </h3>
            <p className="mx-auto max-w-md text-gray-600">
              {currentStepData.description}
            </p>
          </div>

          {/* Step Items */}
          <div className="mb-8 space-y-4">
            {currentStepData.items.map((item, index) => (
              <div key={index} className="flex items-center p-4 space-x-3 bg-gray-50 rounded-lg">
                <div className="flex-shrink-0">
                  <CheckCircle size={20} className="text-green-500" />
                </div>
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex sticky bottom-0 justify-between items-center pt-4 bg-white">
            <button
              onClick={handleSkip}
              className="font-medium text-gray-500 transition-colors hover:text-gray-700"
            >
              Skip for now
            </button>
            
            <div className="flex space-x-3">
              {currentStep > 0 && (
                <button
                  onClick={() => setCurrentStep(currentStep - 1)}
                  className="px-6 py-2 text-gray-700 rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
                >
                  Previous
                </button>
              )}
              
              <button
                onClick={handleNext}
                className="flex items-center px-6 py-2 space-x-2 text-white bg-gradient-to-r from-blue-500 to-green-500 rounded-lg transition-all duration-200 hover:from-blue-600 hover:to-green-600"
              >
                <span>
                  {currentStep === steps.length - 1 ? 'Get Started' : 'Next'}
                </span>
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
          <div className="flex justify-center items-center space-x-6">
            {steps.map((step, index) => (
              <div
                key={step.id}
                className={`flex items-center space-x-2 ${
                  index <= currentStep ? 'text-blue-600' : 'text-gray-400'
                }`}
              >
                <div
                  className={`w-2 h-2 rounded-full ${
                    index <= currentStep ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                />
                <span className="text-sm font-medium">{step.title}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal; 